/**
 * Sentry error reporting for the React shell (#338).
 *
 * Browser errors used to be reported nowhere: Sentry was initialised for Django only. This module
 * owns all of the frontend Sentry configuration so the protected src/main.tsx only has to call
 * initSentry() once.
 *
 * PHI posture (an EMR sends this data to a third party, so the defaults are deliberately strict):
 *  - sendDefaultPii is off, and there is no Session Replay and no performance tracing.
 *  - DOM click/input breadcrumbs and console breadcrumbs are never recorded, because they carry
 *    on-screen text and logged chart data.
 *  - Every URL that leaves the browser has its query string and fragment dropped, and any path
 *    segment that could be an identifier replaced (see scrubUrl). TECDoc paths carry encounter
 *    and patient ids, and query strings carry search terms.
 *  - Free text (error messages, breadcrumb messages) is pattern-scrubbed for emails, SSNs,
 *    phone numbers, dates and embedded URLs.
 *  - The only user context is the login id. Names, usernames and emails are never attached.
 *
 * Reporting is gated: no DSN means no init (local dev), and prod stays off until the PHI review
 * signs it off, even if a DSN is supplied at build time.
 */
import * as Sentry from '@sentry/react';
import type { Breadcrumb, ErrorEvent } from '@sentry/react';

interface ShellWindow {
  appContext?: { environment?: string };
  authContext?: { user?: { id?: number | null } | null };
  tenantContext?: { schema?: string } | null;
}

// Environments where reporting stays off until the PHI/BAA review signs it off (#338).
const PHI_GATED_ENVIRONMENTS = new Set(['prod', 'production']);

const REDACTED_SEGMENT = ':id';
// Route words such as `encounters` or `get_encounter`. Anything else (digits, uuids, tokens,
// percent-encoded text, uppercase codes) may be an identifier and is redacted.
const SAFE_SEGMENT = /^[a-z][a-z_-]*$/;
// Every segment after one of these is a credential, even if it looks like a word.
const SECRET_PATH_PREFIXES = new Set(['reset-password']);

// Same window as the existing /log_javascript_error throttle (TECCore/views.py): a failing
// sync loop can throw every few seconds per open chart and would otherwise burn the quota.
const RATE_LIMIT_MS = 60_000;

const URL_IN_TEXT = /https?:\/\/[^\s'"<>)]+/g;
const TEXT_PATTERNS: Array<[RegExp, string]> = [
  [/[\w.+-]+@[\w-]+\.[\w.-]+/g, '[email]'],
  [/\b\d{3}-\d{2}-\d{4}\b|\b\d{9}\b/g, '[ssn]'],
  [/\(?\b\d{3}\)?[-.\s]\d{3}[-.\s]\d{4}\b/g, '[phone]'],
  [/\b\d{1,2}\/\d{1,2}\/\d{2,4}\b|\b\d{4}-\d{2}-\d{2}\b/g, '[date]'],
];

const lastSentAt = new Map<string, number>();

function shellWindow(): ShellWindow {
  return window as unknown as ShellWindow;
}

/** Strip the query string and fragment and redact identifier-like path segments. */
export function scrubUrl(url: string): string;
export function scrubUrl(url: string | undefined): string | undefined;
export function scrubUrl(url: string | undefined): string | undefined {
  if (!url) return url;
  let parsed: URL;
  try {
    parsed = new URL(url, window.location.origin);
  } catch {
    return '[unparseable url]';
  }
  let redactRest = false;
  const path = parsed.pathname
    .split('/')
    .map((segment) => {
      if (!segment) return segment;
      if (redactRest) return REDACTED_SEGMENT;
      if (SECRET_PATH_PREFIXES.has(segment)) {
        redactRest = true;
        return segment;
      }
      return SAFE_SEGMENT.test(segment) ? segment : REDACTED_SEGMENT;
    })
    .join('/');
  const isAbsolute = /^[a-z][a-z\d+.-]*:/i.test(url);
  return isAbsolute ? `${parsed.origin}${path}` : path;
}

/** Remove the identifier patterns we can recognise from free text. */
export function scrubText(text: string): string;
export function scrubText(text: string | undefined): string | undefined;
export function scrubText(text: string | undefined): string | undefined {
  if (!text) return text;
  const withUrlsScrubbed = text.replace(URL_IN_TEXT, (url) => scrubUrl(url));
  return TEXT_PATTERNS.reduce((result, [pattern, replacement]) => result.replace(pattern, replacement), withUrlsScrubbed);
}

export function scrubBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb | null {
  const category = breadcrumb.category ?? '';
  // Belt and braces: these integrations are switched off in initSentry, but never let them through.
  if (category === 'console' || category.startsWith('ui.')) return null;

  const scrubbed: Breadcrumb = { ...breadcrumb, message: scrubText(breadcrumb.message) };
  if (breadcrumb.data) {
    const data = { ...breadcrumb.data };
    for (const key of ['url', 'from', 'to']) {
      if (typeof data[key] === 'string') data[key] = scrubUrl(data[key]);
    }
    scrubbed.data = data;
  }
  return scrubbed;
}

export function isRateLimited(event: ErrorEvent, now = Date.now()): boolean {
  const exception = event.exception?.values?.[0];
  const signature = `${exception?.type ?? ''}:${exception?.value ?? event.message ?? ''}`;
  const last = lastSentAt.get(signature);
  if (last !== undefined && now - last < RATE_LIMIT_MS) return true;
  lastSentAt.set(signature, now);
  return false;
}

export function resetRateLimit(): void {
  lastSentAt.clear();
}

export function scrubEvent(event: ErrorEvent): ErrorEvent {
  if (event.request) {
    const userAgent = event.request.headers?.['User-Agent'];
    event.request = {
      url: scrubUrl(event.request.url),
      // Referer, cookies, query string and body can all carry identifiers.
      headers: userAgent ? { 'User-Agent': userAgent } : undefined,
    };
  }
  event.message = scrubText(event.message);
  event.exception?.values?.forEach((exception) => {
    exception.value = scrubText(exception.value);
  });
  event.breadcrumbs = event.breadcrumbs
    ?.map(scrubBreadcrumb)
    .filter((breadcrumb): breadcrumb is Breadcrumb => breadcrumb !== null);
  delete event.extra;
  event.user = event.user?.id !== undefined ? { id: event.user.id } : undefined;
  return event;
}

export function beforeSend(event: ErrorEvent): ErrorEvent | null {
  if (isRateLimited(event)) return null;
  return scrubEvent(event);
}

/** Runtime environment from Django (CURRENT_ENV), falling back to the CI build's VITE_ENVIRONMENT. */
export function resolveEnvironment(): string {
  const runtime = shellWindow().appContext?.environment;
  return (runtime || import.meta.env.VITE_ENVIRONMENT || 'dev').toLowerCase();
}

export function shouldInitSentry(
  dsn: string | undefined,
  environment: string,
  buildEnvironment: string | undefined,
): boolean {
  if (!dsn) return false;
  if (PHI_GATED_ENVIRONMENTS.has(environment)) return false;
  if (buildEnvironment && PHI_GATED_ENVIRONMENTS.has(buildEnvironment.toLowerCase())) return false;
  return true;
}

/** Initialise Sentry if this build and environment allow it. Returns whether it was enabled. */
export function initSentry(): boolean {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = resolveEnvironment();
  if (!shouldInitSentry(dsn, environment, import.meta.env.VITE_ENVIRONMENT)) return false;

  Sentry.init({
    dsn,
    environment,
    sendDefaultPii: false,
    // Replaces the default breadcrumbs integration (matched by name). Navigation, fetch and xhr
    // breadcrumbs stay because they explain what led to an error; their URLs are scrubbed.
    integrations: [Sentry.breadcrumbsIntegration({ console: false, dom: false })],
    beforeBreadcrumb: scrubBreadcrumb,
    beforeSend,
  });

  const { authContext, tenantContext } = shellWindow();
  const userId = authContext?.user?.id;
  if (userId) Sentry.setUser({ id: String(userId) });
  if (tenantContext?.schema) Sentry.setTag('tenant', tenantContext.schema);
  return true;
}
